import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { IntegrationErrorGroup } from 'services/api/integrations.api';
import { NAVY, RADIUS_CARD, RADIUS_ITEM, SURFACE_CONTAINER_LOW, SURFACE_HOVER } from 'styles/tokens';

import { statusCodeColor } from '../utils';

interface ErrorSummaryPanelProps {
  errors: IntegrationErrorGroup[];
  onSelect: (statusCode: number | null, error: string | null) => void;
}

const ErrorSummaryPanel: React.FC<ErrorSummaryPanelProps> = ({ errors, onSelect }) => {
  if (errors.length === 0) return null;

  return (
    <Box sx={{ mb: 3, p: 2, borderRadius: RADIUS_CARD, bgcolor: SURFACE_CONTAINER_LOW }}>
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontWeight: 600,
          fontSize: '0.7rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: alpha(NAVY, 0.55),
          mb: 1,
        }}
      >
        Errores frecuentes
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {errors.map((group) => (
          <Box
            key={`${group.statusCode}-${group.endpoint}-${group.error}`}
            onClick={() => onSelect(group.statusCode, group.error)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: RADIUS_ITEM,
              cursor: 'pointer',
              transition: 'background-color 0.18s ease',
              '&:hover': { bgcolor: SURFACE_HOVER },
            }}
          >
            <Chip
              label={group.statusCode ?? 'ERR'}
              size="small"
              sx={{
                bgcolor: alpha(statusCodeColor(group.statusCode), 0.15),
                color: statusCodeColor(group.statusCode),
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 22,
                minWidth: 48,
                borderRadius: RADIUS_ITEM,
              }}
            />
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontFamily: 'monospace',
                fontSize: '0.78rem',
                color: NAVY,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {group.error || group.endpoint}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: '0.85rem',
                color: NAVY,
                flexShrink: 0,
              }}
            >
              {group.count}×
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.7rem',
                color: alpha(NAVY, 0.5),
                flexShrink: 0,
                display: { xs: 'none', md: 'block' },
              }}
            >
              {new Date(group.lastSeen).toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ErrorSummaryPanel;
